import { useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { Users, CheckCircle2, Clock, ClipboardList, AlertTriangle } from "lucide-react";
import { ApiRoutes } from "../../../core/dictionary/api-url";
import axiosService from "../../../core/services/axios-service";
import { getInitials, isRescueRnAdmin } from "../../../core/services/global-functions";
import ToastService from "../../../core/services/toaster-service";
import { ListingHeader } from "../../../components/listing-header";
import { MetricsCard } from "../../../components/designs/matrics-card";
import { RescueRnTable } from "../../../components/designs/rescueRn-table";

export default function OrganizationDrillsPage() {
  const { id } = useParams();
  // The page is mounted under both /admin and /business — keep links on the
  // same prefix the user came in on.
  const basePath = isRescueRnAdmin() ? "/admin" : "/business";

  const [data, setData] = useState([]);
  const [organizationName, setOrganizationName] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [submittedCount, setSubmittedCount] = useState(0);
  const [notSubmittedCount, setNotSubmittedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const res = await axiosService.get(
        `${ApiRoutes.CODEPREP_DRILL_SUBMISSIONS_BY_ORGANIZATION}/${id}`,
        { params: { perPage, currentPage } }
      );
      if (res.data?.success) {
        setData(res.data.data || []);
        setOrganizationName(res.data.organization_name || "");
        setTotalRows(res.data.total || 0);
        setSubmittedCount(res.data.submittedCount || 0);
        setNotSubmittedCount(res.data.notSubmittedCount || 0);
      } else {
        const msg = res.data?.message || "Failed to load submitted drills.";
        setData([]);
        setTotalRows(0);
        setError(msg);
        ToastService.error(msg);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Failed to load submitted drills. Please try again.";
      setData([]);
      setTotalRows(0);
      setError(msg);
      ToastService.error(msg);
    } finally {
      setLoading(false);
    }
  }, [id, perPage, currentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    {
      name: "Name",
      selector: (row) => row.fullName,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <div className="organization-avatar me-3">
            {getInitials(row.fullName || "-")}
          </div>
          {row.fullName || "—"}
        </div>
      ),
      width: "250px",
    },
    {
      name: "Email",
      selector: (row) => row.email,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>{row.email || "—"}</span>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.hasSubmitted,
      cell: (row) =>
        row.hasSubmitted ? (
          <span className="attendance-badge attendance-badge--present">
            <CheckCircle2 size={13} />
            Submitted
          </span>
        ) : (
          <span className="attendance-badge attendance-badge--absent">
            <Clock size={13} />
            Not Submitted
          </span>
        ),
      width: "160px",
    },
    {
      name: "Submissions",
      selector: (row) => row.submissionCount,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {row.submissionCount ?? 0}
        </span>
      ),
      width: "130px",
    },
    {
      name: "Latest Drill Date",
      selector: (row) => row.latestDrillDate,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {row.latestDrillDate
            ? moment(row.latestDrillDate).format("Do MMM YYYY")
            : "—"}
        </span>
      ),
    },
    {
      name: "Last Submitted",
      selector: (row) => row.latestSubmittedAt,
      cell: (row) => (
        <span style={{ color: "var(--brand-slate)" }}>
          {row.latestSubmittedAt
            ? moment(row.latestSubmittedAt).format("Do MMM YYYY, h:mm A")
            : "—"}
        </span>
      ),
    },
  ];

  return (
    <>
      <ListingHeader
        title="Submitted Drills"
        breadCrumb={[
          ...(isRescueRnAdmin()
            ? [
                { label: "Dashboard", href: "/admin/dashboard" },
                { label: "Organizations", href: "/admin/organizations" },
              ]
            : []),
          {
            label: organizationName || "Organization",
            href: `${basePath}/organizations/${id}`,
          },
          { label: "Submitted Drills", href: "#" },
        ]}
        actions={[]}
        dropdown={null}
      />

      <div className="flex-grow-1 p-4">
        <div className="container-fluid p-0">
          <div className="row g-4 mb-4">
            <div className="col-xl-4 col-lg-4">
              <MetricsCard
                title="Total Users"
                value={loading ? "--" : totalRows}
                icon={Users}
              />
            </div>
            <div className="col-xl-4 col-lg-4">
              <MetricsCard
                title="Submitted"
                value={loading ? "--" : submittedCount}
                icon={CheckCircle2}
              />
            </div>
            <div className="col-xl-4 col-lg-4">
              <MetricsCard
                title="Not Submitted"
                value={loading ? "--" : notSubmittedCount}
                icon={ClipboardList}
              />
            </div>
          </div>

          {error && !loading ? (
            <div className="attendance-prompt">
              <AlertTriangle size={44} />
              <p>{error}</p>
              <button
                type="button"
                className="btn btn-rescuern-primary"
                onClick={fetchData}
              >
                Try Again
              </button>
            </div>
          ) : (
            <RescueRnTable
              data={data}
              loading={loading}
              columns={columns}
              totalRows={totalRows}
              loadingTableText="Loading submitted drills..."
              handleSort={() => {}}
              onChangePage={(page) => setCurrentPage(page)}
              handlePerRowsChange={(newPerPage) => {
                setPerPage(newPerPage);
                setCurrentPage(1);
              }}
              perPage={perPage}
            />
          )}
        </div>
      </div>
    </>
  );
}
